import React, { useCallback, useMemo } from 'react';
import * as Styles from './Services.styles';
import { WhiteCardHeader, Title } from '../NewHomeLayoutHandler/NewHomeLayoutHandler.styles';
import withErrorBoundary from '@/utils/errorBoundary';
import { SERVICES_IDS } from './Services.enum';
import { useIsMobile } from '@/hooks/useIsMobile/useIsMobile';
import cardEmailBr from '@/pages/Home/media/services/card-email-br.png';
import cardEmailEs from '@/pages/Home/media/services/card-email-es.png';
import cardEmailMobileBr from '@/pages/Home/media/services/card-email-mobile-br.png';
import cardEmailMobileEs from '@/pages/Home/media/services/card-email-mobile-es.png';
import cardGatorProtectBr from '@/pages/Home/media/services/card-gator-protect-br.png';
import cardGatorProtectEs from '@/pages/Home/media/services/card-gator-protect-es.png';
import cardGatorProtectMobileBr from '@/pages/Home/media/services/card-gator-protect-mobile-br.png';
import cardGatorProtectMobileEs from '@/pages/Home/media/services/card-gator-protect-mobile-es.png';
import cardHostingBr from '@/pages/Home/media/services/card-hosting-br.png';
import cardHostingEs from '@/pages/Home/media/services/card-hosting-es.png';
import cardHostingMobileBr from '@/pages/Home/media/services/card-hosting-mobile-br.png';
import cardHostingMobileEs from '@/pages/Home/media/services/card-hosting-mobile-es.png';
import cardOpenClawBr from '@/pages/Home/media/services/card-open-claw-br.png';
import cardOpenClawEs from '@/pages/Home/media/services/card-open-claw-es.png';
import cardOpenClawMobileBr from '@/pages/Home/media/services/card-open-claw-mobile-br.png';
import cardOpenClawMobileEs from '@/pages/Home/media/services/card-open-claw-mobile-es.png';
import { isBRServer } from '@/utils/Validators/validation';

const IMAGES_BR = {
  [SERVICES_IDS.HOSTING]: {
    desktop: cardHostingBr,
    mobile: cardHostingMobileBr,
  },
  [SERVICES_IDS.GATOR_PROTECT]: {
    desktop: cardGatorProtectBr,
    mobile: cardGatorProtectMobileBr,
  },
  [SERVICES_IDS.OPEN_CLAW]: {
    desktop: cardOpenClawBr,
    mobile: cardOpenClawMobileBr,
  },
  [SERVICES_IDS.EMAIL]: {
    desktop: cardEmailBr,
    mobile: cardEmailMobileBr,
  },
};

const IMAGES_ES = {
  [SERVICES_IDS.HOSTING]: {
    desktop: cardHostingEs,
    mobile: cardHostingMobileEs,
  },
  [SERVICES_IDS.GATOR_PROTECT]: {
    desktop: cardGatorProtectEs,
    mobile: cardGatorProtectMobileEs,
  },
  [SERVICES_IDS.OPEN_CLAW]: {
    desktop: cardOpenClawEs,
    mobile: cardOpenClawMobileEs,
  },
  [SERVICES_IDS.EMAIL]: {
    desktop: cardEmailEs,
    mobile: cardEmailMobileEs,
  },
};

const IMAGES = id => (isBRServer ? IMAGES_BR[id] : IMAGES_ES[id]);

const CARDS = {
  [SERVICES_IDS.HOSTING]: {
    id: SERVICES_IDS.HOSTING,
    image: IMAGES(SERVICES_IDS.HOSTING),
    alt: 'card-hosting',
  },
  [SERVICES_IDS.GATOR_PROTECT]: {
    id: SERVICES_IDS.GATOR_PROTECT,
    image: IMAGES(SERVICES_IDS.GATOR_PROTECT),
    alt: 'card-gator-protect',
  },
  [SERVICES_IDS.OPEN_CLAW]: {
    id: SERVICES_IDS.OPEN_CLAW,
    image: IMAGES(SERVICES_IDS.OPEN_CLAW),
    alt: 'card-open-claw',
  },
  [SERVICES_IDS.EMAIL]: {
    id: SERVICES_IDS.EMAIL,
    image: IMAGES(SERVICES_IDS.EMAIL),
    alt: 'card-email',
  },
};

const Services = ({
  servicesLocale,
  cardsActions,
  hasHosting,
}) => {
  const isMobile = useIsMobile();
  const cardsLocale = servicesLocale?.cards;

  const filteredCards = useMemo(
    () => (hasHosting
      ? Object.values(CARDS).filter(card => card.id !== SERVICES_IDS.HOSTING)
      : Object.values(CARDS).filter(card => card.id !== SERVICES_IDS.GATOR_PROTECT)
    ),
    [hasHosting],
  );

  const handleCardClick = useCallback(
    (cardId) => {
      const action = cardsActions[cardId];
      if (action?.onClick) {
        action.onClick();
      }
    },
    [cardsActions],
  );

  return (
    <Styles.Wrapper data-testid="services-wrapper">
      <WhiteCardHeader>
        <Title>{servicesLocale.title}</Title>
      </WhiteCardHeader>

      <Styles.CardsWrapper>
        {filteredCards.map(card => (
          <Styles.Card key={card.alt} $cardId={card.id}>
            {cardsLocale?.[card.id]?.showTag && (
              <Styles.Tag>{cardsLocale?.[card.id]?.tag}</Styles.Tag>
            )}

            <Styles.InnerCard>
              <Styles.Image
                src={isMobile ? card.image.mobile : card.image.desktop}
                alt={cardsLocale?.[card.id]?.alt ?? card.alt}
                onClick={() => handleCardClick(card.id)}
              />
            </Styles.InnerCard>
          </Styles.Card>
        ))}
      </Styles.CardsWrapper>
    </Styles.Wrapper>
  );
};

export default withErrorBoundary(Services, 'Services');
